import {
  ADDITIONAL_BULK_ACTIONS_FEATURE,
  AMBIENT_SCRIBE_CUSTOM_TEMPLATES,
  CALL_BACK_LIST_FEATURE,
  CENTRALIZED_APPOINTMENT_TEXT_EMAIL_TEMPLATES,
  CHANGE_PASSWORD,
  DISCOUNT_TYPES_FEATURE,
  GOOGLE_CALENDAR_SYNC_FEATURE,
  HL7_INTEGRATION,
  HTML_FORMS_FEATURE,
  MANUFACTURER_ALLOWLIST_FEATURE,
  MCP_CONNECTIONS_FEATURE,
  MEDICAL_SCRIBE_FEATURE,
  PODIUM_INTEGRATION,
  PRACTICE_TAGS_FEATURE,
  PRODUCT_CATEGORIES_FEATURE,
  PURCHASE_AGREEMENT_TEMPLATES_SECTION,
  TAG_MANAGER_FEATURE,
  VERTICAL_SOUTHWESTERN_LIMITATIONS,
  ZOOM_TELEHEALTH_INTEGRATION
} from "constants/feature-flags";
import {
  ADMIN_LEVEL_GROUPS,
  PHYSICIANS_SECTION_GROUPS,
  SCRIBE_SETTINGS_GROUPS
} from "constants/staff";

import type { DestinationAccess } from "./destination.types";
import inSection, { DestinationRow } from "./in-section.util";

const SECTION = "/settings";

const hasVoiceSeat = ({ currentPractice, currentUser }: DestinationAccess) =>
  Boolean(currentPractice?.voice_enabled && currentUser?.voice_enabled);
const onlyAdministratorsCanSetSchedule = ({ flags }: DestinationAccess) =>
  Boolean(flags[VERTICAL_SOUTHWESTERN_LIMITATIONS]);

const PERSONAL_TABS: DestinationRow[] = [
  { to: "personal?tab=personal-details", label: "Personal Details" },
  {
    to: "personal?tab=notification-preferences",
    label: "Notification Preferences"
  },
  {
    to: "personal?tab=report-preferences",
    label: "Audiology Report Preferences"
  },
  {
    to: "personal?tab=security-password",
    label: "Security & Password",
    featureFlag: CHANGE_PASSWORD
  },
  {
    to: "personal?tab=email-signature",
    label: "Email",
    keywords: ["signature"]
  },
  { to: "personal?tab=scheduling", label: "Scheduling" },
  { to: "personal?tab=patient-chart", label: "Patient Chart" }
].map((row) => ({ ...row, trail: ["Personal Settings"] }));

const PRACTICE_TABS: DestinationRow[] = [
  { to: "practice?tab=general-settings", label: "General" },
  { to: "practice?tab=scheduling-settings", label: "Scheduling" },
  { to: "practice?tab=billing-settings", label: "Billing" },
  {
    to: "practice?tab=manufacturers-settings",
    label: "Manufacturers",
    featureFlag: MANUFACTURER_ALLOWLIST_FEATURE
  },
  { to: "practice?tab=noah-settings", label: "Noah" },
  { to: "practice?tab=edocs-settings", label: "E-Documents" },
  { to: "practice?tab=claims-settings", label: "Claims" },
  { to: "practice?tab=ha-settings", label: "Hearing Aids" },
  { to: "practice?tab=patient-intake-settings", label: "Patient Intake" },
  { to: "practice?tab=patient-portal-settings", label: "Patient Portal" },
  {
    to: "practice?tab=notification-templates",
    label: "Notifications",
    featureFlag: CENTRALIZED_APPOINTMENT_TEXT_EMAIL_TEMPLATES
  },
  { to: "practice?tab=external-integrations", label: "Integrations" },
  { to: "practice?tab=security-settings", label: "Security" }
].map((row) => ({
  ...row,
  trail: ["Practice Settings"],
  roles: ADMIN_LEVEL_GROUPS
}));

// The Voice group shows for admins and seat holders on a voice practice; an
// admin passes the first rule below, a seat holder only My Phone's.
const VOICE_PAGES: DestinationRow[] = [
  ...[
    { to: "voice/account-setup", label: "Account Setup" },
    { to: "voice/recording", label: "Call Recording" },
    { to: "voice/business-hours", label: "Business Hours" },
    { to: "voice/greeting-library", label: "Greeting Library" },
    { to: "voice/phone-tree", label: "Phone Tree" },
    { to: "voice/ring-groups", label: "Ring Groups" },
    { to: "voice/closures", label: "Calendar Closures" },
    { to: "voice/shared-voicemail", label: "Shared Voicemail" }
  ].map((row) => ({
    ...row,
    roles: ADMIN_LEVEL_GROUPS,
    when: ({ currentPractice }: DestinationAccess) =>
      Boolean(currentPractice?.voice_enabled)
  })),
  // Hot desking (ENG-2293): a per-practice switch on top of the admin gate.
  {
    to: "voice/desk-phones",
    label: "Desk Phones",
    roles: ADMIN_LEVEL_GROUPS,
    when: ({ currentPractice }: DestinationAccess) =>
      Boolean(
        currentPractice?.voice_enabled && currentPractice?.hot_desking_enabled
      )
  },
  { to: "voice/my-phone", label: "My Phone", when: hasVoiceSeat }
].map((row) => ({ ...row, trail: ["Voice"] }));

const ADMIN_PAGES: DestinationRow[] = [
  { to: "appointment-types", label: "Appointment Types" },
  { to: "appointment-outcome-reasons", label: "Appointment Outcome Reasons" },
  { to: "audiometer-configurations", label: "Audiometer Configurations" },
  { to: "verification-equipment", label: "REM & Test Box Equipment" },
  { to: "referral-sources", label: "Referral Sources" },
  { to: "managed-care-plans", label: "Managed Care Plans" },
  { to: "insurers", label: "Insurers" },
  { to: "taxes", label: "Clinic Taxes" },
  { to: "onboarding-forms", label: "Onboarding Forms" },
  { to: "payment-portal", label: "Payment Processing" },
  { to: "fax-portal", label: "Fax Portal" },
  { to: "external-accounts", label: "External Accounts" },
  {
    to: "template-categories",
    label: "Template Categories",
    featureFlag: PRACTICE_TAGS_FEATURE
  },
  {
    to: "digital-forms",
    label: "Digital Forms",
    featureFlag: HTML_FORMS_FEATURE
  },
  {
    to: "forms",
    label: "Purchase Agreements",
    featureFlag: PURCHASE_AGREEMENT_TEMPLATES_SECTION
  },
  { to: "tags", label: "Tags", featureFlag: PRACTICE_TAGS_FEATURE },
  {
    to: "bulk-action-history",
    label: "Bulk Action History",
    when: ({ flags }) =>
      Boolean(
        flags[ADDITIONAL_BULK_ACTIONS_FEATURE] || flags[TAG_MANAGER_FEATURE]
      )
  },
  {
    to: "call-back-list",
    label: "Call Back List",
    featureFlag: CALL_BACK_LIST_FEATURE
  },
  {
    to: "discount-types",
    label: "Discount Types",
    featureFlag: DISCOUNT_TYPES_FEATURE
  },
  {
    to: "product-categories",
    label: "Product Categories",
    featureFlag: PRODUCT_CATEGORIES_FEATURE
  },
  { to: "podium", label: "Podium", featureFlag: PODIUM_INTEGRATION },
  {
    to: "zoom",
    label: "Zoom Telehealth",
    featureFlag: ZOOM_TELEHEALTH_INTEGRATION
  },
  {
    to: "hl7-integration",
    label: "HL7 Integration",
    featureFlag: HL7_INTEGRATION
  },
  {
    to: "google-reviews",
    label: "Google Reviews",
    when: ({ currentPractice, currentUser }) =>
      Boolean(currentPractice?.google_reviews_enabled) &&
      Boolean(currentUser?.has_access_to_google_reviews)
  }
].map((row) => ({ ...row, roles: ADMIN_LEVEL_GROUPS }));

const OTHER_PAGES: DestinationRow[] = [
  { to: "practice", label: "Practice Settings", roles: ADMIN_LEVEL_GROUPS },
  {
    to: "schedule",
    label: "Schedule",
    when: (access) => !onlyAdministratorsCanSetSchedule(access)
  },
  {
    to: "templates",
    label: "Templates",
    when: (access) =>
      !onlyAdministratorsCanSetSchedule(access) ||
      access.roles.isAdminOrBusinessOwner
  },
  { to: "physicians", label: "Physicians", roles: PHYSICIANS_SECTION_GROUPS },
  {
    to: "scribes",
    label: "Scribe Settings",
    roles: SCRIBE_SETTINGS_GROUPS,
    featureFlag: MEDICAL_SCRIBE_FEATURE,
    when: ({ flags }) => Boolean(flags[AMBIENT_SCRIBE_CUSTOM_TEMPLATES])
  },
  {
    to: "google-calendars",
    label: "Google Calendars",
    featureFlag: GOOGLE_CALENDAR_SYNC_FEATURE
  },
  {
    to: "ai-connections",
    label: "AI Connections",
    keywords: ["mcp", "claude", "chatgpt", "assistant"],
    featureFlag: MCP_CONNECTIONS_FEATURE
  }
];

// The Scheduler role's Settings menu is Personal Settings alone.
const hiddenFromSchedulers = ({
  when,
  ...row
}: DestinationRow): DestinationRow => ({
  ...row,
  when: (access) => !access.roles.isScheduler && (when ? when(access) : true)
});

export const SETTINGS_DESTINATIONS = inSection(SECTION, [
  { to: "personal", label: "Personal Settings" },
  ...PERSONAL_TABS,
  ...[...OTHER_PAGES, ...PRACTICE_TABS, ...ADMIN_PAGES, ...VOICE_PAGES].map(
    hiddenFromSchedulers
  )
]);
